import { useRef, React, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Controller } from "react-hook-form";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Toolbar,
  ExcelExport,
  Group,
  Filter,
  Selection,
  Sort,
  Page,
} from "@syncfusion/ej2-react-grids";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import axios from "axios";
import {
  Box,
  CircularProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import SideBar from "../sideBar/SideBar";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const ButtonStyle = styled.button`
  margin-top: 7px;
  width: 250px;
  height: 55px;
  background-color: #c7131c;
  color: #fff;
  font-weight: bold;
  font-size: 18px;
  border-color: #c7131c;
  border-radius: 5px;
  cursor: pointer;
`;
const ErrorText = styled.p`
  color: red;
`;

export default function ReportRunnerDetails() {
  const schema = yup.object().shape({
    runnerID: yup.number().nullable(),
    fromDate: yup.string().required("من تاريخ مطلوب"),
    toDate: yup.string().required("إلى تاريخ مطلوب"),
  });
  const toYMD = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) {
      // لو الـ API راجع "2025-08-10T00:00:00" أو "2025-08-10" هنقصّ أول 10
      return String(v).slice(0, 10);
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const onRowDataBound = (args) => {
    if (args.data.AssignedOrders < 10) {
      args.row.style.backgroundColor = "#ffe5e5";
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [responseData, setResponseData] = useState([]);
  const [showDataGrid, setShowDataGrid] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    shouldUnregister: false, // ✅ خليه يحتفظ بالقيم حتى مع التعطيل
  });

  const navigate = useNavigate();
  const gridInstance = useRef(null);

  const toolbarClick = (args) => {
    switch (args.item.text) {
      case "Excel Export":
      case "ExcelExport":
        gridInstance.current?.excelExport();
        break;
      case "CSV Export":
      case "CsvExport":
        gridInstance.current?.csvExport();
        break;
      default:
        break;
    }
  };

  const accessToken = localStorage.getItem("userStorage")
    ? JSON.parse(localStorage.getItem("userStorage")).accessToken
    : null;

  const authHeaders = useMemo(
    () => ({
      CompanyID: "241701",
      AccessToken: `${accessToken}`,
      "Content-Type": "application/json",
    }),
    [accessToken]
  );

  const { data: runnerData } = useQuery(
    "runner_list",
    async () => {
      const data = await axios.post(
        "https://anydev.com-eg.net/api/RunnersActivity/V1/GetRunnersListEX/0",
        { pageSize: 10000, pageNumber: 1 },
        { headers: authHeaders }
      );
      return data.data.data;
    },
    {
      onError: (error) => {
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate("/");
        }
      },
    }
  );

  async function regeisterHandler(formValues) {
    const runnerSchema = {
      fromDate: "",
      toDate: "",
      runnerID: 0,
    };
    try {
      setIsLoading(true);
      const response = await axios.post(
        "https://anydev.com-eg.net/api/RunnersActivity/V1/GetRunnersDairyDetails",
        { ...runnerSchema, ...formValues },
        { headers: authHeaders }
      );
      // ✅ احتفظ بنفس قيم الفورم (خصوصًا التاريخين)
      reset(formValues);
      setResponseData(response.data.data || []);
      setShowDataGrid(true);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  }

  // --- ربط القايمتين (بالاسم/بالكود) للفورم الرئيسي
  const [selectedRunnerId, setSelectedRunnerId] = useState("");
  const [selectedRunnerCode, setSelectedRunnerCode] = useState("");

  const handleRunnerNameChange = (e) => {
    const runnerId = e.target.value;
    setSelectedRunnerId(runnerId);
    setValue("runnerID", +runnerId);
    if (runnerId === "0") {
      setSelectedRunnerCode("");
      return;
    }
    const selectedRunner = runnerData?.find(
      (runner) => runner.id === +runnerId
    );
    setSelectedRunnerCode(selectedRunner?.code || "");
  };

  const handleRunnerCodeChange = (e) => {
    const runnerCode = e.target.value;
    setSelectedRunnerCode(runnerCode);
    if (runnerCode === "0") {
      setSelectedRunnerId("0");
      setValue("runnerID", 0);
      return;
    }
    const selectedRunner = runnerData?.find(
      (runner) => runner.code === runnerCode
    );
    setSelectedRunnerId(selectedRunner?.id || "");
    setValue("runnerID", selectedRunner?.id || "");
  };

  // ====== ✏️ مودال التعديل ======
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const {
    handleSubmit: handleEditSubmit,
    register: registerEdit,
    reset: resetEdit,
    formState: { errors: editErrors },
    setValue: setEditValue,
    control: editControl,
  } = useForm({
    defaultValues: {
      runnerID: "",
      loan: 0,
      reward: 0,
      penalty: 0,
      notes: "",
      transDate: "",
    },
    mode: "onTouched",
  });

  const openEdit = (row) => {
    setEditRow(row);
    const runnerName =
      runnerData?.find((r) => r.id === row.runnerID)?.employeeNameA ?? "";
    resetEdit({
      runnerID: row?.runnerId ?? "",
      loan: row?.loan ?? 0,
      reward: row?.reward ?? 0,
      penalty: row?.penalty ?? 0,
      notes: row?.notes ?? "",
      transDate: toYMD(row?.transDate),
      runnerName: runnerName,
    });

    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
  };

  const onEditSave = async (values) => {
    try {
      const payload = {
        transID: editRow.transId,
        runnerID: Number(values.runnerID) || 0,
        loan: Number(values.loan) || 0,
        reward: Number(values.reward) || 0,
        penalty: Number(values.penalty) || 0,
        notes: values.notes || "",
        transDate: toYMD(values?.transDate),
      };
      await axios.post(
        "https://anydev.com-eg.net/api/RunnersActivity/V1/UpdateRunnerDairy",
        payload,
        { headers: authHeaders }
      );

      // حدّث الصف محليًا بسرعة (اختياري) + أعِد الجلب بإرسال نفس الفلتر
      setResponseData((prev) =>
        prev.map((r) =>
          r.transId === editRow.transId
            ? { ...r, ...payload, transId: editRow.transId }
            : r
        )
      );
      closeEdit();
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const onDelete = async (row) => {
    if (!row?.transId) return;
    if (!window.confirm("هل تريد حذف هذه الحركة؟")) return;
    try {
      await axios.post(
        `https://anydev.com-eg.net/api/RunnersActivity/V1/DeleteRunnerDairy/${row.transId}`,
        null,
        { headers: authHeaders }
      );
      setResponseData((prev) => prev.filter((r) => r.transId !== row.transId));
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  // ====== 🔧 عمود الإجراءات كـ Template ======
  const ActionsTemplate = (props) => {
    const row = props; // صف الجدول الحالي
    return (
      <Stack direction="row" spacing={1} justifyContent="center">
        <IconButton
          aria-label="edit"
          size="small"
          onClick={() => openEdit(row)}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
        <IconButton
          aria-label="delete"
          size="small"
          onClick={() => onDelete(row)}
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      </Stack>
    );
  };

  const toolbarOptions = ["ExcelExport", "CsvExport"];

  // ✅ حالة لتثبيت التاريخ
  const [lockDate, setLockDate] = useState(false);

  return (
    <div className="control-pane">
      <SideBar />
      <form onSubmit={handleSubmit(regeisterHandler)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "15px", // مسافة بين الأزرار
            marginRight: "20px",
            flexWrap: "wrap", // للسماح للأزرار بالانتقال إلى سطر جديد في الشاشات الصغيرة
            direction: "rtl",
          }}
        >
          <Box sx={{ marginTop: "7px" }}>
            <TextField
              label="من تاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ width: "250px" }}
              {...register("fromDate")}
              disabled={lockDate}
            />
            <ErrorText>{errors.fromDate?.message}</ErrorText>
          </Box>

          <Box sx={{ marginTop: "7px" }}>
            <TextField
              label="إلى تاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ width: "250px" }}
              {...register("toDate")}
              disabled={lockDate}
            />
            <ErrorText>{errors.toDate?.message}</ErrorText>
          </Box>

          {/* ✅ تشيك بوكس تثبيت التاريخ */}
          <Box sx={{ display: "flex", alignItems: "center", marginTop: "7px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockDate}
                  onChange={(e) => setLockDate(e.target.checked)}
                />
              }
              label="تثبيت التاريخ"
            />
          </Box>

          {/* المندوب بالاسم */}
          <Box sx={{ marginTop: "7px" }}>
            <TextField
              select
              label="إختار المندوب بالاسم"
              sx={{ width: "250px" }}
              value={selectedRunnerId}
              onChange={handleRunnerNameChange}
            >
              <MenuItem value="0">الكل</MenuItem>
              {runnerData?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.arabicName}
                </MenuItem>
              ))}
            </TextField>
            <ErrorText>{errors.runnerID?.message}</ErrorText>
          </Box>

          {/* المندوب بالكود */}
          <Box sx={{ marginTop: "7px" }}>
            <TextField
              select
              label="إختار المندوب بالكود"
              sx={{ width: "250px" }}
              value={selectedRunnerCode}
              onChange={handleRunnerCodeChange}
            >
              <MenuItem value="0">الكل</MenuItem>
              {runnerData?.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.code}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <ButtonStyle type="submit">
            {isLoading ? <CircularProgress /> : `عرض البيانات`}
          </ButtonStyle>
          <ButtonStyle
            className="no-print"
            style={{ backgroundColor: "#007bff", border: "1px solid #007bff" }}
            onClick={() => gridInstance.current.excelExport()}
          >
            تصدير إلى Excel
          </ButtonStyle>
        </Box>
      </form>

      {showDataGrid && (
        <Box
          sx={{ height: 450, width: "98%", margin: "0 auto" }}
          className="col-lg-9 control-section"
        >
          <GridComponent
            dataSource={responseData}
            toolbar={toolbarOptions}
            ref={gridInstance}
            selectionSettings={{ type: "checkbox" }}
            filterSettings={{ type: "Excel" }}
            allowSorting
            allowExcelExport
            allowPdfExport
            enableStickyHeader
            allowFiltering
            allowGrouping
            toolbarClick={toolbarClick}
            rowDataBound={onRowDataBound}
          >
            <ColumnsDirective>
              <ColumnDirective width={40} type="checkbox"></ColumnDirective>

              <ColumnDirective
                headerText="السريال"
                field="transId"
                width="110"
                textAlign="Center"
              ></ColumnDirective>
              <ColumnDirective
                headerText="المندوب"
                field="employeeNameA"
                width="110"
                textAlign="Center"
              ></ColumnDirective>

              <ColumnDirective
                headerText="التاريخ"
                field="transDate"
                width="160"
                textAlign="Center"
                template={(props) => {
                  const date = new Date(props.transDate);
                  const formattedDate = date.toLocaleDateString("en-CA"); // "YYYY-MM-DD"
                  return <span>{formattedDate}</span>;
                }}
              ></ColumnDirective>

              <ColumnDirective
                headerText="سلفة"
                field="loan"
                width="130"
                textAlign="Center"
              ></ColumnDirective>

              <ColumnDirective
                field="reward"
                headerText="مكافأة"
                width="150"
                textAlign="Center"
              ></ColumnDirective>

              <ColumnDirective
                field="penalty"
                headerText="جزاءات"
                width="150"
                textAlign="Center"
              ></ColumnDirective>

              <ColumnDirective
                field="notes"
                headerText="ملاحظات"
                width="220"
                textAlign="Center"
              ></ColumnDirective>

              {/* ✅ عمود الإجراءات */}
              <ColumnDirective
                headerText="الإجراءات"
                width="140"
                textAlign="Center"
                template={ActionsTemplate}
              />
            </ColumnsDirective>

            <Inject
              services={[
                Toolbar,
                ExcelExport,
                Group,
                Page,
                Filter,
                Selection,
                Sort,
              ]}
            />
          </GridComponent>
        </Box>
      )}

      {/* ✅ مودال التعديل */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        fullWidth
        maxWidth="sm"
        dir="rtl"
      >
        <DialogTitle>تعديل الحركة #{editRow?.transId}</DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            onSubmit={handleEditSubmit(onEditSave)}
            sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}
          >
            <TextField
              fullWidth
              label="التاريخ"
              type="date"
              InputLabelProps={{ shrink: true }}
              {...registerEdit("transDate")}
              disabled={lockDate}
            />

            <Controller
              name="runnerID"
              control={editControl}
              render={({ field }) => (
                <Autocomplete
                  fullWidth
                  options={runnerData || []}
                  getOptionLabel={(option) => option.arabicName || ""}
                  value={runnerData?.find((r) => r.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="المندوب"
                      error={!!editErrors.runnerID}
                      helperText={editErrors.runnerID?.message}
                    />
                  )}
                />
              )}
            />
            <ErrorText>{editErrors.runnerID?.message}</ErrorText>

            <TextField
              fullWidth
              label="سلفة"
              type="number"
              {...registerEdit("loan")}
            />
            <TextField
              fullWidth
              label="مكافأة"
              type="number"
              {...registerEdit("reward")}
            />
            <TextField
              fullWidth
              label="جزاء"
              type="number"
              {...registerEdit("penalty")}
            />
            <TextField
              fullWidth
              multiline
              label="ملاحظات"
              {...registerEdit("notes")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>إلغاء</Button>
          <Button onClick={handleEditSubmit(onEditSave)} variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
